import { BreakpointObserver } from '@angular/cdk/layout';

import { DOCUMENT, Injectable, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { StorageManager } from '../storage/storage-manager';

export const THEME_STORAGE_KEY = 'theme';
export const LIGHT_MODE_CLASS_NAME = 'jp-light-theme';
export const DARK_MODE_CLASS_NAME = 'jp-dark-theme';
export const DARK_MODE_MEDIA_QUERY = '(prefers-color-scheme: dark)';

export type ColorScheme = 'dark' | 'light';

@Injectable({
  providedIn: 'root',
})
export class ThemeManager {
  private readonly storage = inject(StorageManager);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly document = inject(DOCUMENT);

  private preferesColorSchemeQuery = toSignal(
    this.breakpointObserver.observe(DARK_MODE_MEDIA_QUERY),
  );

  readonly preferedScheme = computed<ColorScheme>(() =>
    this.preferesColorSchemeQuery()?.matches ? 'dark' : 'light',
  );

  readonly selectedScheme = signal<ColorScheme | null>(this.storage.get(THEME_STORAGE_KEY));

  readonly theme = computed(() => this.selectedScheme() ?? this.preferedScheme());

  constructor() {
    effect(() => {
      const theme = this.selectedScheme();

      if (theme) {
        this.storage.set(THEME_STORAGE_KEY, theme);
      } else {
        this.storage.remove(THEME_STORAGE_KEY);
      }
    });

    effect(() => {
      this.updateDocumentThemeClass();
    });
  }

  private updateDocumentThemeClass(): void {
    const theme = this.theme();
    this.document.body.classList.toggle(DARK_MODE_CLASS_NAME, theme === 'dark');
    this.document.body.classList.toggle(LIGHT_MODE_CLASS_NAME, theme === 'light');
  }
}
